/**
 * Discover's section pieces: the section frame and its head, the loading
 * placeholders, and the three ways a list of items is drawn (a shelf, a grid
 * of cards, a list of rows). Shared by the built-in sections and the saved
 * searches (#2291), so a section of any type looks like the rest of the page.
 *
 * The styles they use live in DiscoverV1Editorial's stylesheet. Nothing here
 * imports `@/canvas`: cover snapshots go through `useModelPreview`.
 */
import {useRef, useState, type KeyboardEvent, type ReactNode} from 'react';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronLeft, faChevronRight, faClock} from '@fortawesome/pro-solid-svg-icons';
import MarkedText from '@/components/common/marking/MarkedText';
import {getDnField} from '@/utils/label';
import {
    labelOf,
    descOf,
    nameLabelOf,
    descLabelOf,
    tagsOf,
    createdAt,
    updatedAt,
    formatShortDate,
    type DiscoverItem,
    type SectionStatus,
} from './useDiscoverData';
import {modelCover, modelRoute, type CoverOf} from './covers';

/**
 * Props that make a non-native card behave as a button: Tab reaches it, Enter
 * and Space activate it (Space is prevented so the page doesn't scroll), and
 * the mouse click is unchanged. The focus ring is `:focus-visible` only, so
 * mouse users see nothing new.
 */
function cardActivation(onActivate: () => void) {
    return {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onActivate,
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate();
            }
        },
    };
}

export function SectionHead({icon, kicker, title, description, actions, filter}: Readonly<{
    icon: IconDefinition;
    kicker: string;
    title: string;
    /** Plain text under the title (a saved search's description). */
    description?: string;
    /** Links and menus on the right of the head. */
    actions?: ReactNode;
    /** A filter box (#2302): beside the actions, or below the title on a phone (CSS, `.has-filter`). */
    filter?: ReactNode;
}>) {
    return (
        <div className={`edit-section-head${filter ? ' has-filter' : ''}`}>
            <div className="min-w-0 flex-1">
                <div className="edit-section-kickrow">
                    <div className="edit-section-kicker">
                        <FontAwesomeIcon icon={icon} className="mr-[5px] text-[10px]"/>
                        {kicker}
                    </div>
                    {actions && <div className="edit-section-actions">{actions}</div>}
                </div>
                <h3 className="edit-section-title mt-1">{title}</h3>
                {description && <p className="edit-section-desc">{description}</p>}
                {filter && <div className="edit-filter">{filter}</div>}
            </div>
        </div>
    );
}

/** An empty or failed section: one line, with "Try again" when it failed. */
export function SectionMessage({children, onRetry}: Readonly<{ children: ReactNode; onRetry?: () => void }>) {
    return (
        <div className="edit-section-msg" role={onRetry ? 'alert' : undefined}>
            <span>{children}</span>
            {onRetry && (
                <button type="button" className="edit-section-link" onClick={onRetry}>Try again</button>
            )}
        </div>
    );
}

/**
 * A titled section whose list loads on its own: a placeholder while loading,
 * a message when it failed or came back empty, else its content.
 */
export function SectionFrame({label, icon, kicker, title, description, actions, status, empty, emptyText, errorText, onRetry, skeleton, className = 'mb-9', children}: Readonly<{
    label: string;
    icon: IconDefinition;
    kicker: string;
    title: string;
    description?: string;
    actions?: ReactNode;
    status: SectionStatus;
    empty: boolean;
    emptyText: ReactNode;
    errorText: string;
    onRetry: () => void;
    skeleton: ReactNode;
    className?: string;
    children: ReactNode;
}>) {
    const renderBody = () => {
        if (status === 'loading') return skeleton;
        if (status === 'error') return <SectionMessage onRetry={onRetry}>{errorText}</SectionMessage>;
        if (empty) return <SectionMessage>{emptyText}</SectionMessage>;
        return children;
    };

    return (
        <section className={className} aria-label={label} aria-busy={status === 'loading'}>
            <SectionHead icon={icon} kicker={kicker} title={title} description={description} actions={actions}/>
            {renderBody()}
        </section>
    );
}

export function ShelfSkeleton() {
    return (
        <div className="edit-shelf" aria-hidden="true">
            {Array.from({length: 4}, (_, i) => (
                <div key={i} className="edit-shelf-card is-skel">
                    <div className="edit-skel" style={{height: 130, borderRadius: 0}}/>
                    <div className="p-3.5">
                        <div className="edit-skel" style={{width: '70%', height: 15}}/>
                        <div className="edit-skel" style={{width: '40%', height: 11, marginTop: 7}}/>
                        <div className="edit-skel" style={{width: '95%', height: 12, marginTop: 12}}/>
                        <div className="edit-skel" style={{width: '80%', height: 12, marginTop: 6}}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function RowsSkeleton() {
    return (
        <div className="flex flex-col gap-1.5" aria-hidden="true">
            {Array.from({length: 4}, (_, i) => (
                <div key={i} className="edit-row is-skel">
                    <span className="edit-skel" style={{width: 36, height: 36, borderRadius: 10}}/>
                    <div className="min-w-0">
                        <div className="edit-skel" style={{width: '40%', height: 14}}/>
                        <div className="edit-skel" style={{width: '65%', height: 11, marginTop: 6}}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function GridSkeleton() {
    return (
        <div className="edit-picks-grid" aria-hidden="true">
            {Array.from({length: 3}, (_, i) => (
                <div key={i} className="edit-pick is-skel" style={{padding: 0, overflow: 'hidden'}}>
                    <div className="edit-skel" style={{height: 110, borderRadius: 0}}/>
                    <div className="p-3.5">
                        <div className="edit-skel" style={{width: '70%', height: 15}}/>
                        <div className="edit-skel" style={{width: '95%', height: 12, marginTop: 12}}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

/** The placeholder for a list drawn as `variant`. */
export function SectionSkeleton({variant}: Readonly<{ variant: 'carousel' | 'grid' | 'list' | 'hero' }>) {
    if (variant === 'list') return <RowsSkeleton/>;
    return variant === 'grid' ? <GridSkeleton/> : <ShelfSkeleton/>;
}

export function HorizontalShelf({items, navigate, cover = modelCover, routeOf = modelRoute}: Readonly<{
    items: DiscoverItem[];
    navigate: (p: string) => void;
    cover?: CoverOf;
    routeOf?: (item: DiscoverItem) => string;
}>) {
    const ref = useRef<HTMLDivElement>(null);
    const [edge, setEdge] = useState<'start' | 'middle' | 'end'>('start');

    function updateEdge() {
        const el = ref.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        if (max <= 1) {
            setEdge('end');
            return;
        }
        if (el.scrollLeft <= 2) setEdge('start');
        else if (el.scrollLeft >= max - 2) setEdge('end');
        else setEdge('middle');
    }

    function scrollBy(delta: number) {
        ref.current?.scrollBy({left: delta, behavior: 'smooth'});
    }

    return (
        <div className="relative">
            <div
                ref={ref}
                className="edit-shelf"
                onScroll={updateEdge}
            >
                {items.map((item, i) => {
                    const desc = descOf(item);
                    const tags = tagsOf(item);
                    const by = item.metadata?.createdBy;
                    const byDisplay = by ? (getDnField(by, 'CN') || by) : '';
                    return (
                        <div
                            key={item._id}
                            className="edit-shelf-card"
                            style={{animationDelay: `${(i % 12) * 40}ms`}}
                            {...cardActivation(() => navigate(routeOf(item)))}
                        >
                            {cover(item)}
                            <div className="p-3.5">
                                <h4 style={{
                                    color: '#e5f1f8',
                                    fontSize: 14.5,
                                    fontWeight: 700,
                                    margin: 0,
                                    lineHeight: 1.3,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}>
                                    <MarkedText label={nameLabelOf(item)} fallback="Untitled"/>
                                </h4>
                                {byDisplay && (
                                    <p className="text-aurora-text-muted/55 text-[11px] mt-[3px] mb-0">
                                        {byDisplay}
                                    </p>
                                )}
                                {desc && (
                                    <p style={{
                                        color: 'rgb(var(--aurora-text-muted) / 0.72)',
                                        fontSize: 12.5,
                                        marginTop: 8,
                                        marginBottom: 0,
                                        lineHeight: 1.5,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}>
                                        <MarkedText label={descLabelOf(item)}/>
                                    </p>
                                )}
                                {tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2.5">
                                        {tags.slice(0, 3).map(t => <span key={t} className="edit-tag">{t}</span>)}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div style={{position: 'absolute', top: -42, right: 0, display: 'flex', gap: 6}}>
                <button className="edit-shelf-btn" onClick={() => scrollBy(-320)} disabled={edge === 'start'}
                        title="Scroll left">
                    <FontAwesomeIcon icon={faChevronLeft} className="text-[11px]"/>
                </button>
                <button className="edit-shelf-btn" onClick={() => scrollBy(320)} disabled={edge === 'end'}
                        title="Scroll right">
                    <FontAwesomeIcon icon={faChevronRight} className="text-[11px]"/>
                </button>
            </div>
        </div>
    );
}

/** A grid of cards with covers and dates: search results, and any list drawn as a grid. */
export function CoverGrid({items, navigate, cover = modelCover, routeOf = modelRoute}: Readonly<{
    items: DiscoverItem[];
    navigate: (p: string) => void;
    cover?: CoverOf;
    routeOf?: (item: DiscoverItem) => string;
}>) {
    return (
        <div className="edit-picks-grid">
            {items.map((item, i) => {
                const desc = descOf(item);
                const tags = tagsOf(item);
                const by = item.metadata?.createdBy;
                const byDisplay = by ? (getDnField(by, 'CN') || by) : '';
                const created = formatShortDate(createdAt(item));
                const updated = formatShortDate(updatedAt(item));
                return (
                    <div
                        key={item._id}
                        className="edit-pick"
                        style={{
                            animationDelay: `${(i % 12) * 30}ms`,
                            padding: 0,
                            overflow: 'hidden'
                        }}
                        {...cardActivation(() => navigate(routeOf(item)))}
                    >
                        {cover(item, 110)}
                        <div className="p-3.5">
                            <h4 style={{
                                color: '#e5f1f8',
                                fontSize: 14.5,
                                fontWeight: 700,
                                margin: 0,
                                lineHeight: 1.3
                            }}><MarkedText label={nameLabelOf(item)} fallback="Untitled"/></h4>
                            {byDisplay && (
                                <p className="text-aurora-text-muted/55 text-[11px] mt-[3px] mb-0">{byDisplay}</p>
                            )}
                            {desc && (
                                <p style={{
                                    color: 'rgb(var(--aurora-text-muted) / 0.72)',
                                    fontSize: 12.5,
                                    marginTop: 8,
                                    marginBottom: 0,
                                    lineHeight: 1.5,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}>
                                    <MarkedText label={descLabelOf(item)}/>
                                </p>
                            )}
                            {tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2.5">
                                    {tags.slice(0, 4).map(t => <span key={t}
                                                                     className="edit-tag">{t}</span>)}
                                </div>
                            )}
                            {(updated || created) && (
                                <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
                                    {updated && (
                                        <span className="edit-date">
                                  <FontAwesomeIcon icon={faClock} className="text-[9px]"/>
                                  Updated {updated}
                                </span>
                                    )}
                                    {created &&
                                        <span className="edit-date">Created {created}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/** The editor's picks grid: text cards, no covers. */
export function PicksGrid({items, navigate, routeOf = modelRoute}: Readonly<{
    items: DiscoverItem[];
    navigate: (p: string) => void;
    routeOf?: (item: DiscoverItem) => string;
}>) {
    return (
        <div className="edit-picks-grid">
            {items.map((item, i) => {
                const desc = descOf(item);
                const tags = tagsOf(item);
                const by = item.metadata?.createdBy;
                const byDisplay = by ? (getDnField(by, 'CN') || by) : '';
                return (
                    <div
                        key={item._id}
                        className="edit-pick"
                        style={{animationDelay: `${(i % 12) * 30}ms`}}
                        {...cardActivation(() => navigate(routeOf(item)))}
                    >
                        <h4 style={{
                            color: '#e5f1f8',
                            fontSize: 15,
                            fontWeight: 700,
                            margin: 0,
                            lineHeight: 1.3
                        }}><MarkedText label={nameLabelOf(item)} fallback="Untitled"/></h4>
                        {byDisplay && (
                            <p className="text-aurora-text-muted/55 text-[11px] mt-1 mb-0">{byDisplay}</p>
                        )}
                        {desc && (
                            <p style={{
                                color: 'rgb(var(--aurora-text-muted) / 0.78)',
                                fontSize: 13,
                                marginTop: 10,
                                marginBottom: 0,
                                lineHeight: 1.55,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}>
                                <MarkedText label={descLabelOf(item)}/>
                            </p>
                        )}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-[5px] mt-3">
                                {tags.slice(0, 3).map(t => <span key={t}
                                                                 className="edit-tag">{t}</span>)}
                                {tags.length > 3 && <span
                                    className="edit-tag opacity-60">+{tags.length - 3}</span>}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/** A compact list of rows: continue editing, and any list drawn as a list. */
export function ItemRows({items, navigate, routeOf = modelRoute}: Readonly<{
    items: DiscoverItem[];
    navigate: (p: string) => void;
    routeOf?: (item: DiscoverItem) => string;
}>) {
    return (
        <div className="flex flex-col gap-1.5">
            {items.map((item, i) => {
                const label = labelOf(item);
                const desc = descOf(item);
                const tags = tagsOf(item);
                return (
                    <div
                        key={item._id}
                        className="edit-row"
                        style={{animationDelay: `${(i % 12) * 25}ms`}}
                        {...cardActivation(() => navigate(routeOf(item)))}
                    >
                        <span
                            className="edit-iconpill">{label.charAt(0).toUpperCase()}</span>
                        <div className="min-w-0">
                            <div title={label} style={{
                                color: '#d4eaf5',
                                fontSize: 14,
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}><MarkedText label={nameLabelOf(item)} fallback="Untitled" truncate/></div>
                            {desc && (
                                <div title={desc} style={{
                                    color: 'rgb(var(--aurora-text-muted) / 0.65)',
                                    fontSize: 12,
                                    marginTop: 2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    <MarkedText label={descLabelOf(item)} truncate/>
                                </div>
                            )}
                        </div>
                        <div className="hidden gap-1 md:flex">
                            {tags.slice(0, 2).map(t => <span key={t}
                                                             className="edit-tag">{t}</span>)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * A list drawn as its section's variant. `native` draws the variant a type
 * was designed around in its own way (the picks' text cards), so a type's
 * default look is exactly what it was before sections were configurable.
 */
export function ItemsView({variant, items, navigate, cover = modelCover, routeOf = modelRoute, picksGrid = false}: Readonly<{
    variant: 'carousel' | 'grid' | 'list' | 'hero';
    items: DiscoverItem[];
    navigate: (p: string) => void;
    cover?: CoverOf;
    routeOf?: (item: DiscoverItem) => string;
    /** Draw a grid as the editor's picks' text cards. */
    picksGrid?: boolean;
}>) {
    if (variant === 'list') return <ItemRows items={items} navigate={navigate} routeOf={routeOf}/>;
    if (variant === 'grid') {
        return picksGrid
            ? <PicksGrid items={items} navigate={navigate} routeOf={routeOf}/>
            : <CoverGrid items={items} navigate={navigate} cover={cover} routeOf={routeOf}/>;
    }
    return <HorizontalShelf items={items} navigate={navigate} cover={cover} routeOf={routeOf}/>;
}
